import React, { useState, useEffect } from 'react';
import { Mail, Phone, Building2, ChevronRight, Search, User, Percent, Pencil, X, Save, Loader2, Clock, Key } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { KYCDocuments } from '../KYCDocuments';

const SHAREHOLDER_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'company', label: 'Company' },
  { value: 'llp', label: 'LLP' },
  { value: 'trust', label: 'Trust' },
];

const DSC_OPTIONS = [
  { value: 'not_applied', label: 'Not Applied' },
  { value: 'applied', label: 'Applied' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
];

const DIN_STATUS_OPTIONS = [
  { value: 'not_applied', label: 'Not Applied' },
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'De-Activated' },
];

// Status Badge Component - Reusable for DIN, DSC, etc.
const StatusBadge = ({ status, type }) => {
  const configs = {
    din: {
      active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
      not_applied: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Not Applied' },
      deactivated: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Deactivated' },
    },
    dsc: {
      active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
      applied: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Applied' },
      not_applied: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Not Applied' },
      expired: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Expired' },
    },
    signatory: {
      true: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Yes' },
      false: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'No' },
    },
    shareholder_type: {
      individual: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Individual' },
      company: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Company' },
      llp: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'LLP' },
      trust: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Trust' },
    }
  };
  
  const config = configs[type]?.[String(status)] || configs[type]?.['not_applied'] || { bg: 'bg-slate-500/20', text: 'text-slate-400', label: status || 'N/A' };
  
  return (
    <span className={`text-xs px-2 py-1 rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Display Field Component - Shows value or hides if empty
const DisplayField = ({ label, value, mono = false, hideIfEmpty = true }) => {
  if (hideIfEmpty && !value) return null;
  
  return (
    <div className="bg-slate-900/50 rounded-lg p-3">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`text-white text-sm ${mono ? 'font-mono' : ''}`}>{value || 'Not provided'}</p>
    </div>
  );
};

// Auto-compute expiry status from a date string
const computeExpiryStatus = (expiryDateStr) => {
  if (!expiryDateStr) return { status: 'unknown', label: 'Unknown', bg: 'bg-slate-500/20', text: 'text-slate-400' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDateStr);
  expiry.setHours(0, 0, 0, 0);
  if (expiry < today) return { status: 'expired', label: 'Expired', bg: 'bg-red-500/20', text: 'text-red-400' };
  const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (daysLeft <= 30) return { status: 'expiring_soon', label: `Expiring (${daysLeft}d)`, bg: 'bg-amber-500/20', text: 'text-amber-400' };
  return { status: 'active', label: 'Active', bg: 'bg-emerald-500/20', text: 'text-emerald-400' };
};

const ComputedStatusBadge = ({ expiryDate, label }) => {
  const info = computeExpiryStatus(expiryDate);
  return (
    <span className={`text-xs px-2 py-1 rounded-full ${info.bg} ${info.text}`} data-testid={`computed-status-${label?.toLowerCase()}`}>{info.label}</span>
  );
};

// Summary Panel for Edit View
const SummaryPanel = ({ sharesCount = 0, holdingPercent = 0, documentsCount = 0 }) => (
  <div className="bg-slate-900/50 rounded-lg p-4 mb-4 grid grid-cols-3 gap-4 border border-slate-700/50">
    <div className="text-center">
      <p className="text-2xl font-bold text-blue-400">{sharesCount.toLocaleString()}</p>
      <p className="text-xs text-slate-500">Shares</p>
    </div>
    <div className="text-center border-x border-slate-700/50">
      <p className="text-2xl font-bold text-emerald-400">{holdingPercent.toFixed(2)}%</p>
      <p className="text-xs text-slate-500">Holding</p>
    </div>
    <div className="text-center">
      <p className="text-2xl font-bold text-amber-400">{documentsCount}</p>
      <p className="text-xs text-slate-500">Documents</p>
    </div>
  </div>
);

// Last Updated Badge Component
const LastUpdatedBadge = ({ updatedAt, updatedByName, updatedByRole }) => {
  if (!updatedAt) return null;
  
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  const getRoleBadge = (role) => {
    if (role === 'super_admin' || role === 'admin') {
      return { text: 'Admin', color: 'bg-purple-500/20 text-purple-400' };
    }
    return { text: 'Client', color: 'bg-blue-500/20 text-blue-400' };
  };
  
  const roleBadge = getRoleBadge(updatedByRole);
  
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-900/50 rounded-lg px-3 py-2 mt-4">
      <Clock className="w-3.5 h-3.5" />
      <span>Last updated {formatDate(updatedAt)}</span>
      {updatedByName && (
        <>
          <span>by</span>
          <span className="font-medium text-slate-400">{updatedByName}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs ${roleBadge.color}`}>
            {roleBadge.text}
          </span>
        </>
      )}
    </div>
  );
};

const ShareholderCard = ({ shareholder, onClick, isSelected }) => {
  const typeColors = {
    individual: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    company: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    llp: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    trust: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };

  // Check if shareholder is from US
  const isUSShareholder = shareholder?.country === 'US' || 
    shareholder?.ssn || 
    shareholder?.itin ||
    shareholder?.company?.country === 'US' ||
    shareholder?.company?.entity_type?.startsWith('us_');

  return (
    <div 
      onClick={onClick}
      className={`bg-slate-800/50 border rounded-xl p-5 cursor-pointer transition-all duration-300 hover:border-indigo-500/50 hover:bg-slate-800 ${
        isSelected ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-slate-700/50'
      }`}
      data-testid={`shareholder-card-${shareholder.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 ${
          isUSShareholder ? 'bg-gradient-to-br from-red-400 to-blue-500' : 'bg-gradient-to-br from-purple-400 to-pink-500'
        }`}>
          {shareholder.shareholder_name?.charAt(0)?.toUpperCase() || 'S'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-white font-semibold truncate">{shareholder.shareholder_name || 'Unknown'}</h3>
            {/* Country badge */}
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              isUSShareholder ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
            }`}>
              {isUSShareholder ? 'USA' : 'India'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${typeColors[shareholder.shareholder_type] || typeColors.individual}`}>
              {shareholder.shareholder_type || 'Individual'}
            </span>
            {shareholder.is_authorized_signatory && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                Signatory
              </span>
            )}
          </div>
          {shareholder.shareholder_email && (
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
              <Mail className="w-4 h-4" />
              <span className="truncate">{shareholder.shareholder_email}</span>
            </div>
          )}
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1">
              <Percent className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-semibold">{shareholder.percent_holding?.toFixed(1) || 0}%</span>
            </div>
            <span className="text-slate-500 text-sm">{shareholder.number_of_shares?.toLocaleString() || 0} shares</span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-500" />
      </div>

      {shareholder.company && (
        <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center gap-2 text-sm">
          <Building2 className="w-4 h-4 text-slate-500" />
          <span className="text-slate-400 truncate">{shareholder.company.company_name}</span>
        </div>
      )}
    </div>
  );
};

const ShareholderDetailPanel = ({ shareholder, company, onRefresh, onNavigateToCompany, onNavigateToDirector }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Check if shareholder is from US
  const isUSShareholder = shareholder?.country === 'US' || 
    shareholder?.ssn || 
    shareholder?.itin ||
    company?.country === 'US' ||
    company?.entity_type?.startsWith('us_');

  // Reset form when shareholder changes
  useEffect(() => {
    if (shareholder) {
      setIsEditing(false);
    }
  }, [shareholder?.id]);

  const handleEdit = () => {
    setEditForm({
      shareholder_name: shareholder?.shareholder_name || '',
      shareholder_email: shareholder?.shareholder_email || '',
      shareholder_type: shareholder?.shareholder_type || 'individual',
      number_of_shares: shareholder?.number_of_shares || 0,
      pan_number: shareholder?.pan_number || '',
      aadhaar_number: shareholder?.aadhaar_number || '',
      dsc_status: shareholder?.dsc_status || 'not_applied',
      dsc_expiry: shareholder?.dsc_expiry || '',
      dsc_expiry_date: shareholder?.dsc_expiry_date || '',
      dsc_class: shareholder?.dsc_class || '',
      dsc_provider: shareholder?.dsc_provider || '',
      is_authorized_signatory: shareholder?.is_authorized_signatory || false,
      din_number: shareholder?.din_number || '',
      din_status: shareholder?.din_status || 'not_applied',
      date_of_birth: shareholder?.date_of_birth || '',
      phone: shareholder?.phone || '',
      address: shareholder?.address || '',
      father_name: shareholder?.father_name || '',
      country: shareholder?.country || 'IN',
      kyc_expiry_date: shareholder?.kyc_expiry_date || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateShareholder(shareholder.id, {
        shareholder_name: editForm.shareholder_name,
        shareholder_email: editForm.shareholder_email,
        shareholder_type: editForm.shareholder_type,
        number_of_shares: parseInt(editForm.number_of_shares) || 0,
        pan_number: editForm.pan_number,
        aadhaar_number: editForm.aadhaar_number,
        dsc_status: editForm.dsc_status,
        dsc_expiry: editForm.dsc_expiry || null,
        dsc_expiry_date: editForm.dsc_expiry_date || null,
        dsc_class: editForm.dsc_class || null,
        dsc_provider: editForm.dsc_provider || null,
        is_authorized_signatory: editForm.is_authorized_signatory,
        din_number: editForm.din_number,
        din_status: editForm.din_status,
        date_of_birth: editForm.date_of_birth || null,
        phone: editForm.phone || null,
        address: editForm.address || null,
        father_name: editForm.father_name || null,
        country: editForm.country || null,
        kyc_expiry_date: editForm.kyc_expiry_date || null,
      });
      toast.success('Shareholder updated successfully');
      setIsEditing(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to update shareholder');
    } finally {
      setSaving(false);
    }
  };

  if (!shareholder) {
    return (
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-8 flex flex-col items-center justify-center text-center h-96">
        <User className="w-16 h-16 text-slate-600 mb-4" />
        <p className="text-slate-400 text-lg">Select a shareholder to view details</p>
        <p className="text-slate-500 text-sm mt-1">Click on any shareholder card to see their information</p>
      </div>
    );
  }

  const companyData = company || shareholder.company;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
      {/* Shareholder Header */}
      <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 p-6 border-b border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-bold text-2xl">
              {shareholder.shareholder_name?.charAt(0)?.toUpperCase() || 'S'}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{shareholder.shareholder_name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge status={shareholder.shareholder_type} type="shareholder_type" />
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 font-semibold">
                  {shareholder.percent_holding?.toFixed(1) || 0}% Holding
                </span>
                <span className="text-xs px-3 py-1 rounded-full bg-slate-700 text-slate-300">
                  {shareholder.number_of_shares?.toLocaleString() || 0} Shares
                </span>
                {shareholder.is_authorized_signatory && (
                  <span className="text-xs px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                    Authorized Signatory
                  </span>
                )}
              </div>
            </div>
          </div>
          {!isEditing ? (
            <Button variant="outline" size="sm" onClick={handleEdit} className="border-slate-600 text-slate-300 hover:bg-slate-700">
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} className="text-slate-400">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Shareholder Info */}
      <div className="p-6 space-y-6 max-h-[calc(100vh-400px)] overflow-y-auto">
        {isEditing ? (
          <div className="space-y-4">
            {/* Summary Panel in Edit Mode */}
            <SummaryPanel 
              sharesCount={shareholder.number_of_shares || 0}
              holdingPercent={shareholder.percent_holding || 0}
              documentsCount={0}
            />

            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Shareholder Name *</label>
                <Input value={editForm.shareholder_name} onChange={(e) => setEditForm({...editForm, shareholder_name: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Shareholder Type</label>
                <select value={editForm.shareholder_type} onChange={(e) => setEditForm({...editForm, shareholder_type: e.target.value})}
                  className="w-full rounded-md bg-slate-900 border-slate-700 text-white p-2 text-sm">
                  {SHAREHOLDER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Email</label>
                <Input type="email" value={editForm.shareholder_email} onChange={(e) => setEditForm({...editForm, shareholder_email: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Phone</label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" placeholder="+91 9876543210" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Date of Birth</label>
                <Input type="date" value={editForm.date_of_birth} onChange={(e) => setEditForm({...editForm, date_of_birth: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Number of Shares *</label>
                <Input type="number" value={editForm.number_of_shares} onChange={(e) => setEditForm({...editForm, number_of_shares: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" min="0" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-slate-500 mb-1 block">Address</label>
                <Input value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Father's Name</label>
                <Input value={editForm.father_name} onChange={(e) => setEditForm({...editForm, father_name: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider pt-4">Identity Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Country</label>
                <select value={editForm.country || 'IN'} onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                  className="w-full rounded-md bg-slate-900 border-slate-700 text-white p-2 text-sm">
                  <option value="IN">India</option>
                  <option value="US">United States</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">PAN Number</label>
                <Input value={editForm.pan_number} onChange={(e) => setEditForm({...editForm, pan_number: e.target.value.toUpperCase()})}
                  className="bg-slate-900 border-slate-700 text-white font-mono" placeholder="ABCDE1234F" maxLength={10} />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Aadhaar Number</label>
                <Input value={editForm.aadhaar_number} onChange={(e) => setEditForm({...editForm, aadhaar_number: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white font-mono" placeholder="1234 5678 9012" maxLength={14} />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider pt-4">DIN & DSC Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">DIN Number</label>
                <Input value={editForm.din_number} onChange={(e) => setEditForm({...editForm, din_number: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white font-mono" placeholder="12345678" maxLength={8} />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">DIN Status</label>
                <select value={editForm.din_status} onChange={(e) => setEditForm({...editForm, din_status: e.target.value})}
                  className="w-full rounded-md bg-slate-900 border-slate-700 text-white p-2 text-sm">
                  {DIN_STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">DSC Status</label>
                <select value={editForm.dsc_status} onChange={(e) => setEditForm({...editForm, dsc_status: e.target.value})}
                  className="w-full rounded-md bg-slate-900 border-slate-700 text-white p-2 text-sm">
                  {DSC_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">DSC Expiry Date</label>
                <Input type="date" value={editForm.dsc_expiry_date || editForm.dsc_expiry} onChange={(e) => setEditForm({...editForm, dsc_expiry_date: e.target.value, dsc_expiry: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">DSC Class</label>
                <select value={editForm.dsc_class} onChange={(e) => setEditForm({...editForm, dsc_class: e.target.value})}
                  className="w-full rounded-md bg-slate-900 border-slate-700 text-white p-2 text-sm">
                  <option value="">Select Class</option>
                  <option value="class_2">Class 2</option>
                  <option value="class_3">Class 3</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">DSC Provider</label>
                <Input value={editForm.dsc_provider} onChange={(e) => setEditForm({...editForm, dsc_provider: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" placeholder="e.g., eMudhra, Sify" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">KYC Expiry Date (DIR-3 KYC)</label>
                <Input type="date" value={editForm.kyc_expiry_date || ''} onChange={(e) => setEditForm({...editForm, kyc_expiry_date: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
                {editForm.kyc_expiry_date && (
                  <div className="mt-1"><ComputedStatusBadge expiryDate={editForm.kyc_expiry_date} label="KYC" /></div>
                )}
              </div>
              <div className="col-span-2 flex items-center gap-3 bg-slate-900/50 rounded-lg p-4">
                <input type="checkbox" id="signatory" checked={editForm.is_authorized_signatory} 
                  onChange={(e) => setEditForm({...editForm, is_authorized_signatory: e.target.checked})}
                  className="w-5 h-5 rounded bg-slate-800 border-slate-600" />
                <label htmlFor="signatory" className="text-sm text-slate-300">Authorized Signatory (can sign documents on behalf of company)</label>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* DISPLAY VIEW - Country-aware */}
            
            {/* Personal Information Section */}
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <DisplayField label="Name" value={shareholder.shareholder_name} hideIfEmpty={false} />
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Type</p>
                  <StatusBadge status={shareholder.shareholder_type} type="shareholder_type" />
                </div>
                <DisplayField label="Email" value={shareholder.shareholder_email} />
                <DisplayField label="Phone" value={shareholder.phone} hideIfEmpty={false} />
                <DisplayField label="Date of Birth" value={shareholder.date_of_birth} hideIfEmpty={false} />
                <DisplayField label="Father's Name" value={shareholder.father_name} hideIfEmpty={false} />
                <div className="col-span-2 bg-slate-900/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Address</p>
                  <p className="text-white text-sm">{shareholder.address || '-'}</p>
                </div>
              </div>
            </div>

            {/* Shareholding Details Section */}
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                {isUSShareholder ? 'Ownership Details' : 'Shareholding Details'}
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">{isUSShareholder ? 'Membership Units' : 'Shares Held'}</p>
                  <p className="text-2xl font-bold text-white">{shareholder.number_of_shares?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">{isUSShareholder ? 'Ownership %' : 'Holding %'}</p>
                  <p className="text-2xl font-bold text-emerald-400">{shareholder.percent_holding?.toFixed(2) || 0}%</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">Signatory</p>
                  <StatusBadge status={shareholder.is_authorized_signatory} type="signatory" />
                </div>
              </div>
            </div>

            {/* US-specific: Identity & Tax Details */}
            {isUSShareholder ? (
              <div>
                <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Identity & Tax Details</h3>
                <div className="grid grid-cols-2 gap-4 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                  {shareholder.ssn && (
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">SSN (Last 4)</p>
                      <p className="text-white text-sm font-mono">***-**-{shareholder.ssn}</p>
                    </div>
                  )}
                  {shareholder.itin && (
                    <DisplayField label="ITIN Number" value={shareholder.itin} mono={true} />
                  )}
                  <div className="bg-slate-900/50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">W-8 Form Provided</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      shareholder.w8_provided ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {shareholder.w8_provided ? 'Yes' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* India-specific: Identity Details Section */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Identity Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Country</p>
                      <p className="text-white text-sm">{shareholder.country === 'US' ? 'United States' : shareholder.country === 'IN' ? 'India' : shareholder.country || 'Not provided'}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3" data-testid="shareholder-pan-display">
                      <p className="text-xs text-slate-500 mb-1">PAN Number</p>
                      <p className="text-white text-sm font-mono">
                        {shareholder.pan_number ? `${shareholder.pan_number.slice(0, 4)}${'*'.repeat(Math.max(0, shareholder.pan_number.length - 5))}${shareholder.pan_number.slice(-1)}` : 'Not provided'}
                      </p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3" data-testid="shareholder-aadhaar-display">
                      <p className="text-xs text-slate-500 mb-1">Aadhaar Number</p>
                      <p className="text-white text-sm font-mono">
                        {shareholder.aadhaar_number ? `XXXX XXXX ${shareholder.aadhaar_number.slice(-4)}` : 'Not provided'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* India-specific: DIN & DSC Details Section */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">DIN & DSC Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">DIN Number</p>
                      <p className="text-white text-sm font-mono">{shareholder.din_number || '-'}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">DIN Status</p>
                      <StatusBadge status={shareholder.din_status} type="din" />
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">DSC Status</p>
                      <StatusBadge status={shareholder.dsc_status} type="dsc" />
                    </div>
                    <DisplayField label="DSC Expiry" value={shareholder.dsc_expiry_date || shareholder.dsc_expiry} hideIfEmpty={false} />
                    <DisplayField label="DSC Class" value={shareholder.dsc_class?.replace('_', ' ')} hideIfEmpty={false} />
                    <DisplayField label="DSC Provider" value={shareholder.dsc_provider} hideIfEmpty={false} />
                    <div className="bg-slate-900/50 rounded-lg p-3" data-testid="shareholder-kyc-status">
                      <p className="text-xs text-slate-500 mb-1">KYC Status (DIR-3 KYC)</p>
                      <ComputedStatusBadge expiryDate={shareholder.kyc_expiry_date} label="KYC" />
                      {shareholder.kyc_expiry_date && (
                        <p className="text-xs text-slate-500 mt-1">Due: {new Date(shareholder.kyc_expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      )}
                    </div>
                    <div className="col-span-2 bg-slate-900/50 rounded-lg p-3" data-testid="shareholder-signatory-display">
                      <p className="text-xs text-slate-500 mb-1">Authorized Signatory</p>
                      <StatusBadge status={shareholder.is_authorized_signatory} type="signatory" />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Linked Company */}
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Linked Company</h3>
              {companyData ? (
                <div 
                  className={`bg-slate-900/50 rounded-lg p-4 border border-slate-700/30 ${onNavigateToCompany ? 'cursor-pointer hover:bg-slate-800 hover:border-blue-500/40 transition-colors' : ''}`}
                  onClick={onNavigateToCompany ? () => onNavigateToCompany(companyData) : undefined}
                  data-testid={`shareholder-company-link-${companyData.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-semibold ${
                      companyData.country === 'US' || companyData.entity_type?.startsWith('us_')
                        ? 'bg-gradient-to-br from-red-500 to-blue-600'
                        : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                    }`}>
                      {companyData.company_name?.charAt(0)?.toUpperCase() || 'C'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${onNavigateToCompany ? 'text-blue-400 hover:text-blue-300' : 'text-white'}`}>{companyData.company_name}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          companyData.country === 'US' || companyData.entity_type?.startsWith('us_')
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-orange-500/20 text-orange-400'
                        }`}>
                          {companyData.country === 'US' || companyData.entity_type?.startsWith('us_') ? '🇺🇸' : '🇮🇳'}
                        </span>
                      </div>
                      <p className="text-slate-500 text-sm capitalize">{companyData.entity_type?.replace(/_/g, ' ')}</p>
                      {companyData.country === 'US' || companyData.entity_type?.startsWith('us_') ? (
                        companyData.ein && <p className="text-xs text-slate-500 font-mono mt-1">EIN: {companyData.ein}</p>
                      ) : (
                        companyData.cin_llpin && <p className="text-xs text-slate-500 font-mono mt-1">CIN: {companyData.cin_llpin}</p>
                      )}
                    </div>
                    {onNavigateToCompany && <ChevronRight className="w-4 h-4 text-slate-500" />}
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No company linked</p>
              )}
            </div>

            {/* Shareholder KYC Documents */}
            <KYCDocuments 
              entityType="shareholder"
              entityId={shareholder.id}
              entityName={shareholder.shareholder_name}
              isAdmin={true}
              onRefresh={onRefresh}
              country={isUSShareholder ? 'US' : 'IN'}
            />

            {/* Last Updated Indicator */}
            <LastUpdatedBadge 
              updatedAt={shareholder.updated_at}
              updatedByName={shareholder.updated_by_name}
              updatedByRole={shareholder.updated_by_role}
            />
          </>
        )}
      </div>
    </div>
  );
};

export const ShareholdersTab = ({ shareholders, loading, onSelectShareholder, selectedShareholder, shareholderCompany, onRefresh, onNavigateToCompany, onNavigateToDirector, onNavigateToOrder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('all'); // 'all', 'IN', 'US'

  // Helper to detect if shareholder is from US
  const isUSShareholderFn = (shareholder) => {
    return shareholder?.country === 'US' || 
           shareholder?.ssn || 
           shareholder?.itin ||
           shareholder?.company?.country === 'US' ||
           shareholder?.company?.entity_type?.startsWith('us_');
  };

  const filteredShareholders = shareholders.filter(shareholder => {
    // Text search filter
    const matchesSearch = shareholder.shareholder_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shareholder.shareholder_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shareholder.pan_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shareholder.company?.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Country filter
    const shareholderCountry = isUSShareholderFn(shareholder) ? 'US' : 'IN';
    const matchesCountry = countryFilter === 'all' || shareholderCountry === countryFilter;
    
    return matchesSearch && matchesCountry;
  });

  // Count shareholders by country
  const countByCountry = {
    all: shareholders.length,
    IN: shareholders.filter(s => !isUSShareholderFn(s)).length,
    US: shareholders.filter(s => isUSShareholderFn(s)).length,
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Shareholders List */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search shareholders by name, email, PAN or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Country Filter */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCountryFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              countryFilter === 'all' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            All ({countByCountry.all})
          </button>
          <button
            onClick={() => setCountryFilter('IN')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              countryFilter === 'IN' 
                ? 'bg-orange-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <span>🇮🇳</span> India ({countByCountry.IN})
          </button>
          <button
            onClick={() => setCountryFilter('US')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              countryFilter === 'US' 
                ? 'bg-blue-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <span>🇺🇸</span> USA ({countByCountry.US})
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-700"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-slate-700 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-slate-700 rounded w-48"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto pr-2">
            {filteredShareholders.map((shareholder) => (
              <ShareholderCard
                key={shareholder.id}
                shareholder={shareholder}
                onClick={() => onSelectShareholder(shareholder)}
                isSelected={selectedShareholder?.id === shareholder.id}
              />
            ))}
            {filteredShareholders.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                {searchTerm ? `No shareholders found matching "${searchTerm}"` : 'No shareholders registered'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Shareholder Details */}
      <ShareholderDetailPanel
        shareholder={selectedShareholder}
        company={shareholderCompany}
        onRefresh={onRefresh}
        onNavigateToCompany={onNavigateToCompany}
        onNavigateToDirector={onNavigateToDirector}
      />
    </div>
  );
};
